import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Pencil, Printer, RotateCcw, ShieldAlert } from "lucide-react";
import { api } from "../../../lib/api";
import { apiError } from "../../../lib/auth";
import { NeonButton, Panel } from "../../common";
import { field } from "../EnterpriseNav";
import { DECKS, lineState } from "./decks";
import { RoiCalculator } from "./RoiCalculator";

const Line = ({ line, st, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [t, setT] = useState(st.text);
  const H = line.h === 1 ? "text-xl font-extrabold" : line.h === 2 ? "label-xs text-brand mt-3" : "text-sm text-white/85";
  return (
    <div className={`group relative pl-3 border-l-2 ${st.ok ? "border-transparent" : "border-amber-400/60 bg-amber-400/[0.04] print:hidden"}`} data-testid={`pitch-line-${line.id}`}>
      {edit ? <div className="py-1"><textarea value={t} onChange={(e) => setT(e.target.value)} rows={2} className={`${field} text-sm`} data-testid={`pitch-edit-input-${line.id}`} />
        <div className="flex gap-2 mt-1"><NeonButton onClick={() => { onSave(t); setEdit(false); }} data-testid={`pitch-edit-save-${line.id}`}>Save</NeonButton><NeonButton variant="ghost" onClick={() => setEdit(false)}>Cancel</NeonButton></div></div>
        : <p className={`${H} py-0.5 pr-16`}>{st.text}</p>}
      {!st.ok && !edit && <p className="text-[11px] text-amber-300/90 flex items-start gap-1 pb-1 print:hidden" data-testid={`pitch-flag-${line.id}`}><ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />UNVERIFIED, excluded from export: {st.why}</p>}
      {st.edited && !edit && <p className="text-[10px] text-white/35 print:hidden">Edited by you · included in export</p>}
      {!edit && <div className="absolute right-0 top-0 hidden group-hover:flex gap-1 print:hidden">
        <button onClick={() => { setT(st.text); setEdit(true); }} className="text-white/40 hover:text-brand p-1" title="Edit" data-testid={`pitch-edit-${line.id}`}><Pencil className="w-3 h-3" /></button>
        {st.edited && <button onClick={() => onSave(null)} className="text-white/40 hover:text-brand p-1" title="Reset" data-testid={`pitch-reset-${line.id}`}><RotateCcw className="w-3 h-3" /></button>}</div>}
    </div>
  );
};

export const PitchStudio = ({ p }) => {
  const [deck, setDeck] = useState("onepager");
  const [d, setD] = useState(null);
  const [prof, setProf] = useState({});
  const [edits, setEdits] = useState({});
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    api.get("/enterprise/pitch/proof").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/enterprise/pitch/profile").then((r) => setProf(r.data)).catch(() => {});
    api.get(`/enterprise/pitch/${p.id}/edits`).then((r) => setEdits(r.data)).catch(() => {});
    api.get("/verify/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [p.id]);
  const vals = useMemo(() => d && ({ ...d.proof, ...prof, founder_title: prof.title, partner: p.name,
    verify_link: cfg.slug ? `${cfg.base_url}/verify?merchant=${cfg.slug}` : null }), [d, prof, p, cfg]);
  if (!d) return <p className="font-mono text-xs text-white/40">loading live proof…</p>;
  if (deck === "roi") return <div><DeckPicker deck={deck} setDeck={setDeck} /><RoiCalculator proof={d.proof} partner={p.name} /></div>;
  const lines = DECKS[deck].lines.map((l) => ({ l, st: lineState(l, vals, d.claims, edits[`${deck}:${l.id}`]) }));
  const ok = lines.filter((x) => x.st.ok);
  const save = async (lid, text) => {
    try { await api.put(`/enterprise/pitch/${p.id}/edits`, { deck, line_id: lid, text }); setEdits((e) => { const n = { ...e }; if (text == null) delete n[`${deck}:${lid}`]; else n[`${deck}:${lid}`] = text; return n; }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copy = () => navigator.clipboard.writeText(ok.map((x) => x.st.text).join("\n")).then(() => toast.success(`Copied ${ok.length} verified lines`));
  return (
    <div>
      <DeckPicker deck={deck} setDeck={setDeck} />
      <div className="flex flex-wrap items-center gap-3 mb-4 print:hidden">
        <span className="text-xs text-white/55" data-testid="pitch-export-count"><Check className="inline w-3 h-3 text-brand" /> {ok.length} of {lines.length} lines export-ready · {lines.length - ok.length} unverified (hidden from print & copy)</span>
        <NeonButton variant="ghost" onClick={() => window.print()} data-testid="pitch-print"><Printer className="w-3 h-3" /> Print / PDF</NeonButton>
        <NeonButton variant="ghost" onClick={copy} data-testid="pitch-copy"><Copy className="w-3 h-3" /> Copy verified text</NeonButton>
      </div>
      <div className="print-root"><Panel title={`${DECKS[deck].label} · ${p.name}`} testid="pitch-deck">
        <div className="space-y-1">{lines.map(({ l, st }) => <Line key={`${deck}-${l.id}-${st.text}`} line={l} st={st} onSave={(t) => save(l.id, t)} />)}</div>
        <p className="text-[10px] text-white/35 mt-4">Numbers are live from real AEGIS merchant data ({d.proof.basis}) as of {new Date(d.proof.generated_at).toLocaleString()}.</p>
      </Panel></div>
    </div>
  );
};

const DeckPicker = ({ deck, setDeck }) => (
  <div className="flex flex-wrap gap-2 mb-4 print:hidden" data-testid="pitch-deck-picker">
    {[...Object.entries(DECKS).map(([k, v]) => [k, v.label]), ["roi", "ROI calculator"]].map(([k, l]) => (
      <button key={k} onClick={() => setDeck(k)} className={`font-mono text-[10px] uppercase tracking-wider border px-2.5 py-1.5 transition-colors ${deck === k ? "border-brand text-brand" : "border-white/15 text-white/60 hover:border-white/40"}`} data-testid={`pitch-deck-${k}`}>{l}</button>))}
  </div>
);
